<template>
  <!-- Sam's side of "What do Sam's stars say about us?": the link in his
       message (?birthday_request=<token>) opens this one screen. The token is
       the permission; answering once reads both charts and tells them both. -->
  <Teleport to="body">
    <div class="br-overlay" data-testid="birthday-request">
      <div class="br">
        <template v-if="state === 'loading'"><p class="br-dim">One moment…</p></template>
        <template v-else-if="state === 'gone'">
          <h3 class="br-title">This link has gone quiet</h3>
          <p class="br-text">It may have been answered already. Your messages will have the reading.</p>
          <button type="button" class="br-btn" @click="$emit('close')">Close</button>
        </template>
        <template v-else-if="state === 'done'">
          <h3 class="br-title" data-testid="birthday-request-done">Thank you</h3>
          <p v-if="sunSign" class="br-sign" data-testid="birthday-request-sign">You’re {{ article(sunSign) }} {{ sunSign }}.</p>
          <p v-if="timeAsk" class="br-text">The full picture of your stars with {{ info.requester_name }} is ready in your Journal.</p>
          <p v-else class="br-text">{{ info.requester_name }} will hear that your stars are in. You'll find what they say about
            you two in your messages.</p>
          <button type="button" class="br-btn" @click="$emit('close')">Close</button>
        </template>
        <!-- A time ask (recipient polish §19, BE kind "time"): the birthday
             kept, the time and place open, "I don't know it" kept. -->
        <template v-else-if="timeAsk">
          <div class="bm-wrap">
            <h3 class="br-title">{{ info.requester_name }} would like to see the full picture of your stars together</h3>
            <p class="br-kept" data-testid="br-kept-date">Your birthday · {{ keptDate }} ✓</p>
          </div>
          <BirthMoment mode="inline" :fields="['time', 'place']" :optional="['time', 'place']" :initial="{ birthdate: info.birth_date }"
            :explain="false" :saving="saving" :server-errors="errors" submit-label="✦ Show us both" lock="Private. Only your signs show."
            @save="answer" />
          <button type="button" class="br-later" @click="$emit('close')">Not now</button>
        </template>
        <template v-else>
          <BirthMoment mode="inline"
            :title="`${info.requester_name} wants to see what your stars say about you two`"
            subtitle="Your birthday is all it takes. A time and place show more."
            :optional="['time', 'place']" :with-name="info.requester_name"
            :saving="saving" :server-errors="errors" submit-label="Share my stars" @save="answer" />
          <button type="button" class="br-later" @click="$emit('close')">Not now</button>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import { getBirthdayRequest, answerBirthdayRequest } from '@/api-service'
import { article, sunSignOfDate, MONTHS, partsFromIso } from '@/utils/birth-ask'

const props = defineProps({ token: { type: String, required: true } })
defineEmits(['close'])

const state = ref('loading')
const info = ref({ requester_name: 'Someone' })
const saving = ref(false)
const errors = ref(null)
// The answer carries no signs: the Sun from the date, never on a cusp day.
const sunSign = ref('')
const timeAsk = computed(() => !!(info.value && info.value.kind === 'time' && info.value.birth_date))
const keptDate = computed(() => {
  const p = partsFromIso((info.value && info.value.birth_date) || '')
  return p.month ? `${MONTHS[Number(p.month) - 1]} ${p.day}, ${p.year}` : ''
})

onMounted(async () => {
  try {
    info.value = await getBirthdayRequest(props.token)
    state.value = info.value.answered ? 'gone' : 'ask'
  } catch (e) {
    state.value = 'gone'
  }
})

async function answer(out) {
  saving.value = true; errors.value = null
  try {
    const birthdate = out.birthdate || (info.value && info.value.birth_date) || ''
    await answerBirthdayRequest(props.token, { birthdate, time: out.time || null, ...(out.place ? { place: out.place } : {}) })
    sunSign.value = timeAsk.value ? '' : sunSignOfDate(birthdate)
    state.value = 'done'
  } catch (e) {
    const r = e && e.response
    if (r && r.status === 410) state.value = 'gone'
    else errors.value = (r && r.data && r.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}
</script>

<style scoped>
.br-overlay { position: fixed; inset: 0; z-index: 1400; background: rgba(6,0,14,0.86); display: flex;
  align-items: center; justify-content: center; padding: 16px 12px; overflow-y: auto; }
.br { width: 100%; max-width: 520px; max-height: calc(100vh - 32px); overflow-y: auto; color: #e9dcff; }
.br-title { font-family: 'Cinzel', serif; color: #f5e8c0; margin: 0 0 8px; font-size: 18px; }
.br-text { margin: 0 0 14px; line-height: 1.5; }
.br-dim { color: #9b86c9; }
.br-kept { margin: 0 0 10px; font: 500 15px Inter, system-ui, sans-serif; color: #ffe7b5; }
.br-sign { margin: 0 0 10px; font-family: 'Cinzel', serif; color: #f0d9a3; font-size: 16px; }
.br-btn { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: none; border-radius: 999px;
  padding: 10px 22px; min-height: 44px; font-weight: 700; cursor: pointer; font-family: inherit; }
.br-later { display: block; margin: 12px auto 0; background: none; border: none; color: #9b86c9; text-decoration: underline;
  min-height: 44px; cursor: pointer; font-family: inherit; }
</style>
